import React, { useMemo, useRef, useEffect, useState } from "react";

import { prepareFilters } from "./js/prepareFilters";
import { prepareMarkets } from "./js/prepareMarkets/prepareMarkets";
import type { NavItemProps } from "components/src/components/Nav/components/NavItem/types";
import {
  MarketTemplateProps,
  MarketTemplateRecordType,
} from "./types";
import { randomIdString } from "shared/src/utils/randomIdString";
import { useCompetitionType } from "shared/src/hooks/useCompetitionType";

// Templates
import { SixPack } from "./templates/SixPack";
import { NColGrouped } from "./templates/NColGrouped";
import { SliderMarket } from "./templates/SliderMarket";
import { Squares } from "./templates/Squares";
import { Scrollable } from "./templates/Scrollable";

import { prepareMarketTemplateHeader } from "./js/prepareMarketTemplateHeader";
// Slider / MarketTabs / SelectionTabs / AlternativeSixPack / combined / stacked / squares

const TEMPLATES: MarketTemplateRecordType = {
  sixpack: {
    name: "sixpack",
    useAvatar: false,
    template: SixPack,
  },
  standard: {
    name: "standard",
    useAvatar: false,
    template: NColGrouped,
  },
  markettabs: {
    name: "markettabs",
    useAvatar: false,
    template: NColGrouped,
  },
  selectiontabs: {
    name: "selectiontabs",
    useAvatar: false,
    template: NColGrouped,
  },
  combined: {
    name: "combined",
    useAvatar: true,
    template: NColGrouped,
  },
  stacked: {
    name: "stacked",
    useAvatar: true,
    template: NColGrouped,
  },
  rowgrouped: {
    name: "rowgrouped",
    useAvatar: true,
    template: NColGrouped,
  },
  slider: {
    name: "slider",
    useAvatar: false,
    template: SliderMarket,
  },
  squares: {
    name: "squares",
    useAvatar: false,
    template: Squares,
  },
  scrollable: {
    name: "scrollable",
    useAvatar: false,
    template: Scrollable,
  },
};
const nColTemplates = Object.entries(TEMPLATES)
  .filter(([name, data]) => {
    return data.template === NColGrouped;
  })
  .map(([name, data]) => name);

export const MarketTemplate = (props: MarketTemplateProps) => {
  const defaultId = useRef(`cui__market-template-${randomIdString()}`);
  const {
    id = defaultId.current,
    markets = [],
    type = "standard",
    filters = [],
    filterDisplayAs = "shortname",
    sport: sportProp,
    league: leagueProp,
    competition: competitionProp,
    competitionType,
    eventDetails,
    skeleton = false,
    mode,
    animation = true,
    preventLeagueDetection,
  } = props;

  const { sport, league } = useCompetitionType({ sport: sportProp, league: leagueProp, competition: competitionProp, competitionType });

  const _league = useMemo(
    () =>
      !preventLeagueDetection
        ? checkLeagueIsCollege(
            [
              props?.header?.title, 
              props?.eventDetails?.eventName,
              props?.marketHeader?.description,
            ],
            league
          )
        : league,
    [
      league,
      props?.header?.title,
      props?.eventDetails?.eventName,
      props?.marketHeader?.description,
    ]
  );
  
  // Prepare Filter - memoize the filters and get the related TeamInfo  
  const _filters = useMemo(
    () => prepareFilters(filters, sport, type, mode, filterDisplayAs, league, competitionType),
    [filters, sport, type, mode, filterDisplayAs, league, competitionType]
  ) as NavItemProps[];

  const _eventDetails = { sport, ...eventDetails, league: _league };

  // Prepare Markets, this does the most heavy lifting and wrapping of the provided markets
  const [_markets, _updateMarket, _updateMarkets] = prepareMarkets({
    markets,
    eventDetails: _eventDetails,
    type,
    useAvatar: TEMPLATES[type]?.useAvatar,
    animation,
  });

  // Prepare MarketHeader
  const _header = prepareMarketTemplateHeader({
    header: props?.header,
    hasSGP: props?.hasSGP,
    isOddsBoost: props?.isOddsBoost,
    initiallyOpen: props?.initiallyOpen,
    animation: props?.animation,
    boostLogoType: props?.boostLogoType,
    onHeaderClick: props?.onHeaderClick
  });

  const _type =
    nColTemplates.includes(type) &&
    markets?.length &&
    markets[0].selections?.length > 3 &&
    type !== "rowgrouped"
      ? "scrollable"
      : type;

  if (_type in TEMPLATES) {
    return TEMPLATES[_type].template({
      ...props,
      type: _type,
      id,
      sport,
      league: _league,
      competitionType,
      markets: _markets,
      filters: _filters,
      header: _header,
      updateMarket: _updateMarket,
      eventDetails: _eventDetails,
    });
  }
  return null;
};

/**
 * Check if league is college - Can be removed with Team Metadata v2
 * @param titles - Array of the 3 title types in a MarketTemplate
 * @param league - The passed in league
 * @returns "college" || league
 */
function checkLeagueIsCollege(titles, league) {
  if (typeof league !== "undefined")
    return league;
  return titles.some(
    (title) => typeof title === "string" && (title?.includes("NCAA") || (title?.includes("College") && !title?.includes("College Dublin")))
  )
    ? "college"
    : league;
}
